import type { CustomElement } from 'uinix-ui/lib/components/element';

import {
  createElement,
  forwardRef,
  memo,
  type ElementType,
  type NamedExoticComponent,
  type ReactNode,
  type RefAttributes,
} from 'react';
import { Element } from 'uinix-ui';

import { SharedBoxProps } from '../../types/SharedBoxProps';
import { transformProps } from './utils/transformProps';

export const Box = memo(
  forwardRef<HTMLElement, SharedBoxProps>(function Box(props, ref) {
    const { as = 'div', ...rest } = transformProps(props);

    if (!ref) {
      return <Element as={as} {...rest} />;
    }

    // uinix-ui's `Element` is a plain function component, so a ref attached
    // to it directly is silently dropped by React. `as` also accepts a
    // CustomElement render function that `Element` invokes itself, bypassing
    // React's ref handling — use that to attach the ref to the real host node.
    // uinix-ui mistypes `children` here as `CustomElement`; at runtime it's
    // the real rendered ReactNode `Element` passes through.
    const withRef: CustomElement = (elementProps, children) =>
      createElement(
        as as ElementType,
        { ...elementProps, ref },
        children as ReactNode,
      );

    return <Element {...rest} as={withRef} />;
  }),
  // `SharedBoxProps`'s `[key: string]: any` index signature makes
  // `forwardRef`'s internal `PropsWithoutRef` collapse the whole prop type to
  // `{ [x: string]: any }`, silently erasing every declared prop's type. Cast
  // back to the real prop type to restore type-checking on callers.
) as NamedExoticComponent<SharedBoxProps & RefAttributes<HTMLElement>>;

Box.displayName = 'Box';
